// src/App.js
import React, { useState, useEffect } from 'react';
import './App.css';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Dashboard from './components/pages/Dashboard';
import PriceAnalysis from './components/pages/PriceAnalysis';
import OIMonitoring from './components/pages/OIMonitoring';
import DailyChangeTracker from './components/pages/DailyChangeTracker';
import Alerts from './components/pages/Alerts';
import Portfolio from './components/pages/Portfolio';
import Settings from './components/pages/Settings';
import Help from './components/pages/Help';
import About from './components/pages/About';
import PairTrading from './components/pages/PairTrading';
import Toast from './components/Toast';
import Modal from './components/Modal';
import { useToast } from './hooks/useToast';
import { usePriceAnalyzer } from './hooks/usePriceAnalyzer';
import { useOIMonitor } from './hooks/useOIMonitor';
import { useDailyChangeTracker } from './hooks/useDailyChangeTracker';
import { useAlerts } from './hooks/useAlerts';
import { useSettings } from './hooks/useSettings';

function App() {
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  
  // Custom hooks
  const { toasts, showToast, removeToast } = useToast();
  const priceAnalyzer = usePriceAnalyzer();
  const oiMonitor = useOIMonitor();
  const dailyTracker = useDailyChangeTracker();
  const alertManager = useAlerts();
  const settingsManager = useSettings();

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const wasMobile = isMobile;
      const newIsMobile = window.innerWidth <= 768;
      setIsMobile(newIsMobile);
      
      if (wasMobile !== newIsMobile) {
        if (newIsMobile) {
          // On mobile, start collapsed
          setSidebarCollapsed(true);
        } else if (window.innerWidth > 1024) {
          // On desktop, show sidebar
          setSidebarCollapsed(false);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    
    // Initial check
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  // Initialize application
  useEffect(() => {
    const initApp = async () => {
      try {
        console.log('🚀 Initializing Binance Analytics Pro...');
        
        // Initialize components
        await priceAnalyzer.init();
        await oiMonitor.init();
        await dailyTracker.init();
        alertManager.init();
        settingsManager.init();
        
        // Show welcome message
        showToast('success', 'System Ready', 'Binance Analytics Pro is ready to use');
        
        console.log('✅ Binance Analytics Pro initialized successfully');
      } catch (error) {
        console.error('❌ Failed to initialize application:', error);
        showToast('error', 'Initialization Failed', 'Please refresh the page');
      }
    };

    initApp();

    // Set up periodic updates
    const updateInterval = setInterval(() => {
      setLastUpdate(new Date());
    }, 30000);

    return () => clearInterval(updateInterval);
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + Number for quick page navigation
      if ((e.ctrlKey || e.metaKey) && e.key >= '1' && e.key <= '9') {
        e.preventDefault();
        const pages = ['dashboard', 'price-analysis', 'oi-monitoring', 'daily-tracker', 'alerts', 'portfolio', 'settings', 'help', 'about'];
        const pageIndex = parseInt(e.key) - 1;
        if (pages[pageIndex]) {
          setCurrentPage(pages[pageIndex]);
        }
      }
      
      // F5 or Ctrl+R to refresh current page
      if (e.key === 'F5' || (e.ctrlKey && e.key === 'r')) {
        e.preventDefault();
        refreshCurrentPage();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentPage]);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isMobile && !sidebarCollapsed) {
        const sidebar = document.querySelector('.sidebar');
        const sidebarToggle = document.querySelector('.sidebar-toggle');
        
        if (sidebar && !sidebar.contains(e.target) && !sidebarToggle?.contains(e.target)) {
          setSidebarCollapsed(true);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile, sidebarCollapsed]);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const switchPage = (pageId) => {
    console.log(`📄 Switching to page: ${pageId}`);
    setCurrentPage(pageId);
    
    // Always close mobile sidebar after navigation
    if (isMobile) {
      setSidebarCollapsed(true);
    }
    
    setLastUpdate(new Date());
  };

  const refreshCurrentPage = async () => {
    try {
      switch(currentPage) {
        case 'dashboard':
          // Dashboard refresh logic
          break;
        case 'price-analysis':
          await priceAnalyzer.refreshData();
          break;
        case 'oi-monitoring':
          await oiMonitor.refreshData();
          break;
        case 'daily-tracker':
          await dailyTracker.fetchAllData();
          break;
        case 'alerts':
          alertManager.refreshAlerts();
          break;
        case 'settings':
          settingsManager.loadSettings();
          break;
        default:
          break;
      }
      
      setLastUpdate(new Date());
      showToast('success', 'Refreshed', 'Page data updated successfully');
      
    } catch (error) {
      console.error('Failed to refresh page:', error);
      showToast('error', 'Refresh Failed', 'Unable to update page data');
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const renderCurrentPage = () => {
    const pageProps = {
      priceAnalyzer,
      oiMonitor,
      dailyTracker,
      alertManager,
      settingsManager,
      showToast,
      onPageChange: switchPage
    };

    switch(currentPage) {
      case 'dashboard':
        return <Dashboard {...pageProps} />;
      case 'price-analysis':
        return <PriceAnalysis {...pageProps} />;
      case 'oi-monitoring':
        return <OIMonitoring {...pageProps} />;
      case 'daily-tracker':
        return <DailyChangeTracker {...pageProps} />;
      case 'alerts':
        return <Alerts {...pageProps} />;
      case 'portfolio':
        return <Portfolio {...pageProps} />;
      case 'settings':
        return <Settings {...pageProps} />;
      case 'help':
        return <Help {...pageProps} />;
      case 'about':
        return <About {...pageProps} />;
      case 'pairtrading':
        return <PairTrading {...pageProps} />;
      default:
        return <Dashboard {...pageProps} />;
    }
  };

  const getPageTitle = (pageId) => {
    const titles = {
      'dashboard': 'Dashboard',
      'price-analysis': 'Price Analysis',
      'oi-monitoring': 'OI Monitoring',
      'daily-tracker': 'Daily Change Tracker',
      'alerts': 'Alert Center',
      'portfolio': 'Portfolio',
      'settings': 'Settings',
      'help': 'Help & FAQ',
      'about': 'About'
    };
    return titles[pageId] || pageId;
  };

  const getBreadcrumb = (pageId) => {
    const paths = {
      'dashboard': ['Home', 'Dashboard'],
      'price-analysis': ['Home', 'Analytics', 'Price Analysis'],
      'oi-monitoring': ['Home', 'Analytics', 'OI Monitoring'],
      'daily-tracker': ['Home', 'Analytics', 'Daily Tracker'],
      'alerts': ['Home', 'Management', 'Alerts'],
      'portfolio': ['Home', 'Management', 'Portfolio'],
      'settings': ['Home', 'System', 'Settings'],
      'help': ['Home', 'Support', 'Help'],
      'about': ['Home', 'Support', 'About']
    };
    return paths[pageId] || ['Home'];
  };

  return (
    <div className="App">
      <Sidebar 
        currentPage={currentPage}
        collapsed={sidebarCollapsed}
        onPageChange={switchPage}
        alertCount={alertManager.getUnreadCount()}
        isMobile={isMobile}
      />
      
      <main className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Header 
          title={getPageTitle(currentPage)}
          breadcrumb={getBreadcrumb(currentPage)}
          onToggleSidebar={toggleSidebar}
          onRefresh={refreshCurrentPage}
          onToggleFullscreen={toggleFullscreen}
          totalSymbols={priceAnalyzer.symbols?.length || 0}
          analysisCount={priceAnalyzer.analysisCount || 0}
          lastUpdate={lastUpdate}
          alertCount={alertManager.getUnreadCount()}
        />
        
        <div className="page-content">
          {renderCurrentPage()}
        </div>
      </main>

      {/* Toast Notifications */}
      <div className="toast-container">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            {...toast}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
      
      {/* Mobile overlay when sidebar is open */}
      {isMobile && !sidebarCollapsed && (
        <div 
          className="mobile-overlay" 
          onClick={() => setSidebarCollapsed(true)}
        />
      )}
    </div>
  );
}

export default App;